import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CircleCheck, Globe, HeartHandshake, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";
import { FadeIn, MaskedLines, Chapter, EASE } from "@/components/motion";
import MarqueeBand from "@/components/MarqueeBand";
import CtaSection from "@/components/CtaSection";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { TRUST_PILLARS, STEPS, TREATMENTS, WHY_INDIA, FAQS } from "@/data/content";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <>
      <SEO
        title="Treatment in India for International Patients | GetWellSoon"
        description="GetWellSoon coordinates medical travel to India for international patients — hospital appointments, travel, accommodation, and dedicated patient support from first call to safe return."
      />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-pearl" data-testid="hero-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 pb-24 pt-14 md:px-8 md:pt-20 lg:grid-cols-12 lg:items-center lg:pb-32">
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="inline-flex items-center gap-2.5 rounded-full border border-border bg-white px-4 py-2"
              data-testid="hero-overline"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-sage" />
              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand">
                For international patients & families
              </span>
            </motion.div>

            <MaskedLines
              delay={0.15}
              className="mt-8 font-serif text-4xl leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl"
              lines={[
                "Considering",
                "Treatment",
                <span key="l3">
                  in <span className="italic text-brand">India?</span>
                </span>,
              ]}
            />

            <FadeIn delay={0.65}>
              <p className="mt-7 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
                GetWellSoon helps international patients and families coordinate
                hospital appointments, medical travel, accommodation, and
                on-ground support in India.
              </p>
              <p
                className="mt-5 flex items-start gap-2.5 text-xs font-semibold leading-relaxed text-sage-dark"
                data-testid="hero-serving-line"
              >
                <Globe className="mt-0.5 h-4 w-4 shrink-0" />
                Supporting patients from North America, Europe, the United
                Kingdom, Australia, the Middle East, Africa, and other
                international destinations.
              </p>
            </FadeIn>

            <FadeIn delay={0.8} className="mt-9 flex flex-wrap gap-4">
              <Button
                asChild
                className="h-12 rounded-full bg-brand px-7 font-semibold text-white transition-colors duration-300 hover:bg-brand-dark"
              >
                <Link to="/contact" data-testid="hero-primary-cta">
                  Speak with a Care Coordinator
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="h-12 rounded-full border-ink/15 bg-white px-7 font-semibold text-ink transition-colors duration-300 hover:border-brand hover:text-brand"
              >
                <Link to="/contact" data-testid="hero-secondary-cta">
                  Request a Treatment Estimate
                </Link>
              </Button>
            </FadeIn>

            <FadeIn delay={0.95}>
              <p className="mt-8 flex items-start gap-2.5 text-xs leading-relaxed text-muted-foreground" data-testid="hero-reassurance">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-sage-dark" />
                Coordination services only — all medical decisions remain with
                the treating hospital and its physicians.
              </p>
            </FadeIn>
          </div>

          <div className="relative lg:col-span-6">
            <div className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-sage-light" />
            <motion.div style={{ y: imgY }} className="relative">
              <div className="overflow-hidden rounded-[2rem] rounded-tr-[6rem] border border-border">
                <motion.img
                  src="/images/family-hospital.jpg"
                  alt="A physician reviewing treatment options with a patient and her family"
                  className="h-[380px] w-full object-cover md:h-[520px]"
                  style={{ scale: imgScale }}
                  data-testid="hero-image"
                />
              </div>
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.7, ease: EASE }}
                className="absolute -bottom-8 left-4 max-w-[19rem] rounded-2xl border border-border bg-white p-5 shadow-sm md:-left-10"
                data-testid="hero-floating-card"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sage-light text-sage-dark">
                    <HeartHandshake className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-ink">
                      One dedicated coordinator
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      A single point of contact, from your first call to your
                      safe return home.
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST PILLARS */}
      <section className="py-24 md:py-32" data-testid="trust-section">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Chapter index="01" label="Why families choose us" />
          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:items-end">
            <MaskedLines
              as="h2"
              onView
              className="font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:col-span-7 lg:text-5xl"
              lines={["Built on clarity,", "not promises."]}
            />
            <FadeIn delay={0.2} className="lg:col-span-5">
              <p className="text-base leading-relaxed text-muted-foreground">
                We do not sell treatment and we do not make medical claims. We
                organize the journey honestly, so families can focus on the
                decision in front of them.
              </p>
            </FadeIn>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TRUST_PILLARS.map((p, i) => (
              <FadeIn
                key={p.title}
                delay={i * 0.08}
                className="group rounded-3xl border border-border bg-white p-8 transition-colors duration-300 hover:border-brand/40"
                data-testid={`trust-pillar-${i}`}
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-light text-sage-dark transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                  <p.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-serif text-xl tracking-tight text-ink">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {p.text}
                </p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <MarqueeBand />

      {/* HOW IT WORKS PREVIEW */}
      <section className="py-24 md:py-32" data-testid="home-how-it-works">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Chapter index="02" label="The process" />
          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:items-end">
            <MaskedLines
              as="h2"
              onView
              className="font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:col-span-8 lg:text-5xl"
              lines={["A clear path from your first", "question to your return home."]}
            />
            <FadeIn delay={0.2} className="lg:col-span-4 lg:text-right">
              <Button
                asChild
                variant="outline"
                className="rounded-full border-ink/15 bg-white px-6 font-semibold hover:border-brand hover:text-brand"
              >
                <Link to="/how-it-works" data-testid="home-how-it-works-link">
                  See how it works
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </FadeIn>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {STEPS.map((s, i) => (
              <FadeIn
                key={s.title}
                delay={i * 0.08}
                className="group rounded-3xl border border-border bg-white p-6 transition-colors duration-300 hover:border-brand/40"
                data-testid={`home-step-${i + 1}`}
              >
                <span className="font-serif text-4xl italic text-sage-light transition-colors duration-300 group-hover:text-sage">
                  0{i + 1}
                </span>
                <s.icon className="mt-6 h-6 w-6 text-brand" />
                <h3 className="mt-3 font-serif text-lg leading-snug tracking-tight text-ink">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {s.short}
                </p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* TREATMENTS PREVIEW */}
      <section className="bg-white py-24 md:py-32" data-testid="home-treatments">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Chapter index="03" label="Treatment areas" />
          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:items-end">
            <MaskedLines
              as="h2"
              onView
              className="font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:col-span-8 lg:text-5xl"
              lines={["Specialties we coordinate", "with leading hospitals."]}
            />
            <FadeIn delay={0.2} className="lg:col-span-4 lg:text-right">
              <Button
                asChild
                variant="outline"
                className="rounded-full border-ink/15 px-6 font-semibold hover:border-brand hover:text-brand"
              >
                <Link to="/treatments" data-testid="home-treatments-link">
                  View all treatment areas
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </FadeIn>
          </div>

          <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {TREATMENTS.slice(0, 4).map((t, i) => (
              <FadeIn
                key={t.slug}
                delay={i * 0.08}
                className="group rounded-3xl border border-border bg-pearl p-8 transition-colors duration-300 hover:border-brand/40 hover:bg-white"
                data-testid={`home-treatment-${t.slug}`}
              >
                <t.icon className="h-7 w-7 text-brand" />
                <h3 className="mt-5 font-serif text-xl tracking-tight text-ink">
                  {t.name}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {t.description}
                </p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* WHY INDIA PREVIEW */}
      <section className="py-24 md:py-32" data-testid="home-why-india">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 md:px-8 lg:grid-cols-12">
          <div className="relative lg:col-span-5">
            <FadeIn className="relative">
              <div className="pointer-events-none absolute -left-8 -bottom-8 h-36 w-36 rounded-full bg-brand-light" />
              <div className="relative overflow-hidden rounded-[2rem] rounded-bl-[5rem] border border-border">
                <img
                  src="/images/hospital-building.jpg"
                  alt="The entrance of a modern international hospital"
                  className="h-[420px] w-full object-cover md:h-[520px]"
                  data-testid="home-why-india-image"
                />
              </div>
            </FadeIn>
          </div>
          <div className="lg:col-span-7 lg:pl-8">
            <Chapter index="04" label="Why India" />
            <MaskedLines
              as="h2"
              onView
              className="mt-8 font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl"
              lines={["A measured look at", "treatment in India."]}
            />
            <div className="mt-10 space-y-6">
              {WHY_INDIA.map((r, i) => (
                <FadeIn key={r.title} delay={i * 0.06} className="flex items-start gap-4">
                  <CircleCheck className="mt-1 h-5 w-5 shrink-0 text-sage-dark" />
                  <div>
                    <p className="font-semibold text-ink">{r.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {r.short}
                    </p>
                  </div>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.3}>
              <Button
                asChild
                variant="outline"
                className="mt-10 rounded-full border-ink/15 bg-white px-6 font-semibold hover:border-brand hover:text-brand"
              >
                <Link to="/why-india" data-testid="home-why-india-link">
                  Why patients consider India
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-24 md:py-32" data-testid="faq-section">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 md:px-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Chapter index="05" label="Questions, answered" />
            <MaskedLines
              as="h2"
              onView
              className="mt-8 font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl"
              lines={["Honest answers,", "before you commit."]}
            />
            <FadeIn delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">
                The questions international families ask us most — answered
                plainly. For anything else, a care coordinator is one message
                away.
              </p>
              <Button
                asChild
                className="mt-8 h-12 rounded-full bg-brand px-7 font-semibold text-white hover:bg-brand-dark"
              >
                <Link to="/contact" data-testid="faq-cta-button">
                  Ask us anything
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </FadeIn>
          </div>
          <div className="lg:col-span-7">
            <FadeIn delay={0.1}>
              <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                {FAQS.map((f, i) => (
                  <AccordionItem
                    key={i}
                    value={`item-${i}`}
                    className="border-b border-border"
                    data-testid={`faq-item-${i}`}
                  >
                    <AccordionTrigger
                      className="py-6 text-left font-serif text-lg tracking-tight text-ink hover:no-underline md:text-xl"
                      data-testid={`faq-trigger-${i}`}
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-6 text-base leading-relaxed text-muted-foreground">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </FadeIn>
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
